import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { eachDayOfInterval, endOfDay, format, startOfDay, startOfMonth, subDays } from "date-fns";
import { useLocationContext, useProfileContext } from "context";
import { attemptGetLiveConnectionStats } from "api/OrgApi";
import { getContactsStats, getEmailActivityStats } from "api/bMarketingApi";
import { useGetOrgLocationsMetrics } from "hooks/app/useGetOrgLocationsByUser";
import { useGetDashboardStats } from "hooks/integrations/useGetDashboardStats";

const EMAIL_FILTERS = { channel: "email", section: "overview", metric: "", campaign_id: "" };

export const HOME_PERIODS = {
  today: { label: "Today", detail: "Today" },
  yesterday: { label: "Yesterday", detail: "Yesterday" },
  last7: { label: "Last 7 days", detail: "Last 7 days" },
  month: { label: "This month", detail: "This month" },
};

export function getHomePeriod(periodKey, now = new Date()) {
  const today = startOfDay(now);
  if (periodKey === "today") return { from: today, to: endOfDay(today) };
  if (periodKey === "yesterday") {
    const yesterday = subDays(today, 1);
    return { from: yesterday, to: endOfDay(yesterday) };
  }
  if (periodKey === "month") return { from: startOfMonth(today), to: endOfDay(today) };
  return { from: subDays(today, 6), to: endOfDay(today) };
}

const finiteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export function countRecentReviews(series, startDate, endDate) {
  if (!Array.isArray(series)) return null;
  const from = format(startDate, "yyyy-MM-dd");
  const to = format(endDate, "yyyy-MM-dd");
  return series.reduce((total, point) => (
    point.date >= from && point.date <= to
      ? total + (finiteNumber(point.count) ?? 0)
      : total
  ), 0);
}

export function sumMetricSeries(metric, startDate, endDate) {
  const series = metric?.series?.daily;
  if (!Array.isArray(series)) return null;
  const from = format(startDate, "yyyy-MM-dd");
  const to = format(endDate, "yyyy-MM-dd");
  return series.reduce((total, point) => {
    const date = point.date || point.label;
    return date >= from && date <= to ? total + (finiteNumber(point.count ?? point.value) ?? 0) : total;
  }, 0);
}

export function buildActivitySeries({ startDate, endDate, connections = [], emailSeries = [], reviewSeries = [] }) {
  const values = new Map(eachDayOfInterval({ start: startDate, end: endDate }).map((date) => [
    format(date, "yyyy-MM-dd"),
    { date: format(date, "yyyy-MM-dd"), connections: 0, emailOpens: 0, reviews: 0 },
  ]));
  connections.forEach((connection) => {
    const rawDate = connection.connection_start_time || connection.start_time;
    if (!rawDate) return;
    const key = format(new Date(rawDate), "yyyy-MM-dd");
    if (values.has(key)) values.get(key).connections += 1;
  });
  emailSeries.forEach((point) => {
    const key = point.date || point.label;
    if (values.has(key)) values.get(key).emailOpens += finiteNumber(point.count ?? point.value) ?? 0;
  });
  reviewSeries.forEach((point) => {
    if (values.has(point.date)) values.get(point.date).reviews += finiteNumber(point.count) ?? 0;
  });
  return Array.from(values.values());
}

export function buildHomeRecommendations({ pendingReplies, totalContacts, optedInContacts, sentEmails, newGuests, periodLabel = "the selected period" }) {
  const recommendations = [];

  if (pendingReplies > 0) {
    recommendations.push({
      id: "reply-to-reviews",
      category: "Reviews",
      tone: "orange",
      title: `${pendingReplies.toLocaleString()} ${pendingReplies === 1 ? "review needs" : "reviews need"} a reply`,
      description: "Keep the conversation going. Thank your guests and respond to feedback on Google.",
      actionLabel: "Respond to reviews",
      to: "/reviews/reviews",
    });
  }

  if (optedInContacts > 0 && sentEmails === 0) {
    recommendations.push({
      id: "engage-your-audience",
      category: "Email marketing",
      tone: "violet",
      title: "Say hello to your audience",
      description: `${optedInContacts.toLocaleString()} contacts have opted in to email. Plan an update or offer; no emails were sent during ${periodLabel.toLowerCase()}.`,
      actionLabel: "Plan a campaign",
      to: "/marketing/campaigns",
    });
  } else if (totalContacts === 0) {
    recommendations.push({
      id: "build-your-audience",
      category: "Email marketing",
      tone: "violet",
      title: "Start building your audience",
      description: "Add your contacts and their consent preferences so you can send your first campaign.",
      actionLabel: "Manage audience",
      to: "/marketing/audience",
    });
  } else {
    recommendations.push({
      id: "personalise-your-next-campaign",
      category: "Email marketing",
      tone: "violet",
      title: "Make your next email more personal",
      description: "Group your audience into segments and send a message that fits each group.",
      actionLabel: "Explore your audience",
      to: "/marketing/audience",
    });
  }

  recommendations.push({
    id: "refresh-your-guest-welcome",
    category: "Guest Wi-Fi",
    tone: "blue",
    title: "Make a great first connection",
    description: newGuests > 0
      ? `${newGuests.toLocaleString()} new guests connected during ${periodLabel.toLowerCase()}. Keep your Wi-Fi welcome page fresh and on brand.`
      : "Review your guest welcome page, check your branding and make it easy for guests to stay in touch.",
    actionLabel: "Review guest portal",
    to: "/guest-wifi/captive-portal",
  });

  recommendations.push({
    id: "review-business-profile",
    category: "Business profile",
    tone: "green",
    title: "Help guests find the right information",
    description: "Check your business description, opening hours and contact details before your guests visit.",
    actionLabel: "Review business profile",
    to: "/reviews/business-profile",
  });

  return recommendations;
}

/**
 * Home is an overview of all locations available to this user, independent of
 * filters left on another page. Audience and email APIs are organisation-wide.
 */
export default function useHomeDashboardData(periodInput = "last7") {
  const { userProfile } = useProfileContext();
  const { locationsData, isLocationsSuccess, isLocationsLoading, refetchLocations } = useLocationContext();
  const organisationId = userProfile?.organisation_id;
  const userId = userProfile?.user_id;
  const locationIds = useMemo(() => (
    (locationsData || []).map((location) => Number(location.id)).filter(Number.isFinite).sort((a, b) => a - b)
  ), [locationsData]);
  const periodKey = typeof periodInput === "string" ? periodInput : "custom";
  const period = useMemo(() => (
    typeof periodInput === "string" ? getHomePeriod(periodInput) : periodInput
  ), [periodInput]);
  const customPeriodLabel = period?.from && period?.to
    ? `${format(period.from, "d MMM")} – ${format(period.to, "d MMM yyyy")}`
    : HOME_PERIODS.last7.label;
  const periodLabel = HOME_PERIODS[periodKey]?.label || customPeriodLabel;
  const periodDetail = HOME_PERIODS[periodKey]?.detail || customPeriodLabel;
  const hasLocations = isLocationsSuccess && locationIds.length > 0;
  const noLocations = isLocationsSuccess && locationIds.length === 0;
  const locationsFailed = Boolean(userId) && !isLocationsLoading && !isLocationsSuccess;
  const waitingForLocations = !userId || isLocationsLoading;

  const livePayload = useMemo(() => ({
    request_type: "live-connections-metrics",
    payload: { location_ids: locationIds },
  }), [locationIds]);
  const liveQuery = useQuery({
    queryKey: ["liveConnectionStats", livePayload],
    queryFn: () => attemptGetLiveConnectionStats(livePayload),
    enabled: hasLocations,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
  const metricsPayload = useMemo(() => ({
    payload: {
      location_ids: locationIds,
      user_id: userId,
      start_time: period.from.valueOf(),
      end_time: period.to.valueOf(),
      // This API treats [] as an explicitly empty filter; null means all guests.
      guest_status: null,
      age: null,
      gender: null,
      zone_ids: [],
    },
  }), [locationIds, userId, period]);
  const wifiQuery = useGetOrgLocationsMetrics(metricsPayload, "connection_metric_card", hasLocations);
  const reviewParams = useMemo(() => ({ organisationId, locationIds }), [organisationId, locationIds]);
  const reviewQuery = useGetDashboardStats(reviewParams, hasLocations);
  // Share cache keys with the marketing pages, retaining data presence and
  // allowing the overview to refresh when Home is revisited or focused.
  const contactsQuery = useQuery({
    queryKey: ["contactsStats", organisationId],
    queryFn: () => getContactsStats(organisationId).then((response) => response.data),
    enabled: Boolean(organisationId),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const emailQuery = useQuery({
    // A 90-day response gives us daily series data for exact Today,
    // Yesterday and calendar-month filtering without extra requests.
    queryKey: ["emailActivityStats", organisationId, EMAIL_FILTERS, 90],
    queryFn: () => getEmailActivityStats(organisationId, 90, EMAIL_FILTERS).then((response) => response.data),
    enabled: Boolean(organisationId),
    staleTime: 60_000,
    retry: false,
  });

  const wifiStats = wifiQuery.data;
  const reviewStats = reviewQuery.data?.data?.stats;
  const contactsReady = Boolean(organisationId && contactsQuery.data) && !contactsQuery.isError;
  const emailReady = Boolean(organisationId && emailQuery.data) && !emailQuery.isError;
  const totalContacts = contactsReady ? finiteNumber(contactsQuery.data.total_contacts) : null;
  const optedInContacts = contactsReady ? finiteNumber(contactsQuery.data.email_opted_in) : null;
  const metricFor = (key) => emailQuery.data?.metrics?.[key];
  const seriesSent = emailReady ? sumMetricSeries(metricFor("sent"), period.from, period.to) : null;
  const seriesDelivered = emailReady ? sumMetricSeries(metricFor("delivered"), period.from, period.to) : null;
  const seriesOpened = emailReady ? sumMetricSeries(metricFor("opened"), period.from, period.to) : null;
  const seriesClicked = emailReady ? sumMetricSeries(metricFor("clicked"), period.from, period.to) : null;
  // Keep compatibility with older API responses while daily series rolls out.
  const useSummaryFallback = periodKey === "last7" && !emailQuery.data?.metrics;
  const sentEmails = useSummaryFallback ? finiteNumber(emailQuery.data?.sent) : seriesSent;
  const deliveredEmails = useSummaryFallback ? finiteNumber(emailQuery.data?.delivered) : seriesDelivered;
  const openedEmails = useSummaryFallback ? finiteNumber(emailQuery.data?.opened) : seriesOpened;
  const clickedEmails = useSummaryFallback ? finiteNumber(emailQuery.data?.clicked) : seriesClicked;
  const openRate = useSummaryFallback
    ? finiteNumber(emailQuery.data?.open_rate)
    : deliveredEmails > 0 && openedEmails !== null ? openedEmails / deliveredEmails * 100 : null;
  const clickRate = useSummaryFallback
    ? finiteNumber(emailQuery.data?.click_rate)
    : openedEmails > 0 && clickedEmails !== null ? clickedEmails / openedEmails * 100 : null;
  const totalReviews = noLocations ? 0 : finiteNumber(reviewStats?.total_reviews);
  const pendingReplies = noLocations ? 0 : finiteNumber(reviewStats?.pending_replies);
  const negativeReviews = noLocations ? 0 : finiteNumber(reviewStats?.negative_reviews);
  const newGuests = noLocations ? 0 : finiteNumber(wifiStats?.unique_new_guests);
  const measuredLiveConnections = finiteNumber(liveQuery.data?.data?.data?.live_connections);
  const liveConnections = noLocations
    ? 0
    : hasLocations && !liveQuery.isPending && !liveQuery.isError
      ? measuredLiveConnections ?? 0
      : null;
  const guestConnections = noLocations ? 0 : finiteNumber(wifiStats?.guest_connections);
  const activitySeries = buildActivitySeries({
    startDate: period.from,
    endDate: period.to,
    connections: wifiStats?.connection_details || [],
    emailSeries: metricFor("opened")?.series?.daily || [],
    reviewSeries: reviewStats?.reviews_over_time || [],
  });
  const recentReviews = noLocations ? 0 : countRecentReviews(reviewStats?.reviews_over_time, period.from, period.to);

  const wifi = {
    isLoading: waitingForLocations || (hasLocations && (wifiQuery.isPending || liveQuery.isPending)),
    isError: locationsFailed || wifiQuery.isError || liveQuery.isError,
    isEmpty: noLocations || (guestConnections === 0 && liveConnections === 0),
    emptyMessage: noLocations ? "Add a location to start welcoming guests." : "Your guest connections will appear here.",
    metrics: [
      { id: "connected", label: "Connected now", value: liveConnections, format: "number" },
      { id: "unique", label: "Unique guests", value: noLocations ? 0 : finiteNumber(wifiStats?.unique_guests), format: "number", detail: periodDetail },
      { id: "new", label: "New guests", value: newGuests, format: "number", detail: periodDetail },
      { id: "connections", label: "Total connections", value: guestConnections, format: "number", detail: periodDetail },
    ],
    refetch: () => locationsFailed
      ? refetchLocations()
      : hasLocations && Promise.all([wifiQuery.refetch(), liveQuery.refetch()]),
  };

  const email = {
    isLoading: !organisationId || contactsQuery.isPending || emailQuery.isPending,
    isError: contactsQuery.isError || emailQuery.isError,
    isEmpty: totalContacts === 0 && sentEmails === 0,
    emptyMessage: "Grow your audience and send your first email campaign.",
    sent: sentEmails,
    optedInContacts,
    metrics: [
      { id: "contacts", label: "Total contacts", value: totalContacts, format: "number", detail: "Current organisation audience" },
      { id: "opt-in", label: "Email opt-in rate", value: totalContacts > 0 && optedInContacts !== null ? optedInContacts / totalContacts * 100 : null, format: "percent", detail: "Of your current audience" },
      { id: "open-rate", label: "Open rate", value: deliveredEmails === 0 ? 0 : deliveredEmails > 0 ? openRate : null, format: "percent", detail: `${periodDetail} · of delivered emails` },
      { id: "click-rate", label: "Click-to-open rate", value: openedEmails === 0 ? 0 : openedEmails > 0 ? clickRate : null, format: "percent", detail: `${periodDetail} · of opened emails` },
    ],
    refetch: () => organisationId && Promise.all([contactsQuery.refetch(), emailQuery.refetch()]),
  };

  const reviews = {
    isLoading: waitingForLocations || (hasLocations && reviewQuery.isPending),
    isError: locationsFailed || reviewQuery.isError,
    isEmpty: totalReviews === 0,
    emptyMessage: "Your guest reviews will appear here as they sync.",
    pendingReplies,
    metrics: [
      { id: "rating", label: "Average rating", value: totalReviews > 0 ? finiteNumber(reviewStats?.average_rating) : null, format: "rating", detail: "All time · connected platforms" },
      { id: "recent", label: "New reviews", value: recentReviews, format: "number", detail: periodDetail },
      { id: "total", label: "Total reviews", value: totalReviews, format: "number", detail: "All time" },
      { id: "pending", label: "Awaiting a reply", value: pendingReplies, format: "number", detail: "All time · Google reviews", tone: pendingReplies > 0 ? "orange" : undefined },
      { id: "negative", label: "Negative reviews", value: negativeReviews, format: "number", detail: "All time · connected platforms", tone: negativeReviews > 0 ? "orange" : undefined, wide: true },
    ],
    refetch: () => locationsFailed ? refetchLocations() : hasLocations && reviewQuery.refetch(),
  };

  return {
    wifi,
    email,
    reviews,
    locationCount: locationIds.length,
    periodKey,
    periodLabel,
    activity: {
      series: activitySeries,
      totals: {
        connections: guestConnections,
        emailOpens: openedEmails,
        reviews: recentReviews,
      },
      isLoading: wifi.isLoading || email.isLoading || reviews.isLoading,
      isError: wifi.isError || email.isError || reviews.isError,
    },
    recommendations: buildHomeRecommendations({
      pendingReplies: reviews.isError ? null : pendingReplies,
      totalContacts,
      optedInContacts,
      sentEmails,
      newGuests: wifi.isError ? null : newGuests,
      periodLabel,
    }),
  };
}
